import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import DashboardErrorBoundary from './components/shared/DashboardErrorBoundary';
import NotFound from './components/shared/NotFound';
import SessionExpiredNotification from './components/shared/SessionExpiredNotification';
import AccountDeactivatedModal from './components/shared/AccountDeactivatedModal';
import RouteFallback from './components/shared/RouteFallback';
import Home from './pages/Home';
import TradersDirectory from './pages/TradersDirectory';

const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const About = lazy(() => import('./pages/marketing/about'));
const Contact = lazy(() => import('./pages/marketing/contact'));
const Terms = lazy(() => import('./pages/marketing/Terms'));
const JoinHub = lazy(() => import('./pages/auth/JoinHub'));
const Register = lazy(() => import('./pages/auth/register'));
const RegisterBusiness = lazy(() => import('./pages/auth/registerBusiness'));
const RegisterTrader = lazy(() => import('./pages/auth/RegisterTrader'));
const Login = lazy(() => import('./pages/auth/login'));
const LoginBusiness = lazy(() => import('./pages/auth/LoginBusiness'));
const LoginTrader = lazy(() => import('./pages/auth/LoginTrader'));
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'));
const BusinessRegistration = lazy(() => import('./components/business/BusinessRegistration'));
const BusinessPendingStatus = lazy(() => import('./components/business/BusinessPendingStatus'));
const BusinessAccessGate = lazy(() => import('./components/business/BusinessAccessGate'));
const TraderPublicLanding = lazy(() => import('./components/TraderPublicLanding'));
const MerchantPanel = lazy(() => import('./components/merchant/MerchantPanel'));
const MerchantPendingStatus = lazy(() => import('./components/merchant/MerchantPendingStatus'));
const SupplierHome = lazy(() => import('./components/merchant/SupplierHome'));

const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
const AdminBlogEditor = lazy(() => import('./components/admin/AdminBlogEditor'));
const AdminBlogList = lazy(() => import('./components/admin/AdminBlogList'));
const BusinessPanel = lazy(() => import('./components/business/BusinessPanel'));
const BusinessList = lazy(() => import('./components/admin/BusinessList'));
const BusinessEdit = lazy(() => import('./components/admin/BusinessEdit'));
const BusinessProducts = lazy(() => import('./components/business/BusinessProducts'));
const AddProduct = lazy(() => import('./components/business/AddProduct'));
const BusinessAnalytics = lazy(() => import('./components/business/BusinessAnalytics'));
const UserList = lazy(() => import('./components/admin/UserList'));
const SystemSettings = lazy(() => import('./components/admin/SystemSettings'));
const UserDashboard = lazy(() => import('./components/user/UserDashboard'));
const UserProfile = lazy(() => import('./components/user/UserProfile'));
const AdminTraderFlows = lazy(() => import('./components/admin/AdminTraderFlows'));

function LazyRoute({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { t } = useTranslation();
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="dash-canvas flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-hairline border-t-signal"></div>
          <p className="text-steel">{t('auth.checking')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (!user.id || !user.role) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppContent() {
  const { accountDeactivated, setAccountDeactivated } = useAuth();
  const location = useLocation();
  const { i18n } = useTranslation();

  const isDashboard = location.pathname.startsWith('/admin') ||
                     location.pathname.startsWith('/business') ||
                     location.pathname.startsWith('/user') ||
                     location.pathname.startsWith('/merchant') ||
                     location.pathname.startsWith('/supplier');

  const isTraderStorefront = /^\/trader\/[^/]+$/.test(location.pathname);
  const showMarketingChrome = !isDashboard && !isTraderStorefront;

  const isLTR = i18n.language?.startsWith('en');

  return (
    <div
      className={`relative ${showMarketingChrome ? 'select-none' : ''}`}
      dir={showMarketingChrome ? (isLTR ? 'ltr' : 'rtl') : undefined}
      lang={showMarketingChrome ? (isLTR ? 'en' : (i18n.language?.startsWith('ar') ? 'ar' : 'fa')) : undefined}
    >
      <ScrollToTop />
      <SessionExpiredNotification />
      <AccountDeactivatedModal open={accountDeactivated} onClose={() => setAccountDeactivated(false)} />
      {!showMarketingChrome ? null : <Navbar />}
      <div className={showMarketingChrome ? 'pt-[5.25rem] md:pt-[5.75rem]' : undefined}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<LazyRoute><ServicesPage /></LazyRoute>} />
        <Route path="/traders" element={<TradersDirectory />} />
        <Route path="/services" element={<Navigate to="/products" replace />} />
        <Route path="/service/:id" element={<LazyRoute><ProductDetail /></LazyRoute>} />
        <Route path="/service/:businessSlug/:productSlug" element={<LazyRoute><ProductDetail /></LazyRoute>} />
        <Route path="/service/*" element={<LazyRoute><ProductDetail /></LazyRoute>} />
        <Route path="/blog" element={<LazyRoute><BlogList /></LazyRoute>} />
        <Route path="/blog/:slug" element={<LazyRoute><BlogPost /></LazyRoute>} />
        <Route path="/about" element={<LazyRoute><About /></LazyRoute>} />
        <Route path="/contact" element={<LazyRoute><Contact /></LazyRoute>} />
        <Route path="/terms" element={<LazyRoute><Terms /></LazyRoute>} />
        <Route path="/join" element={<LazyRoute><JoinHub /></LazyRoute>} />
        <Route path="/register/trader" element={<LazyRoute><RegisterTrader /></LazyRoute>} />
        <Route path="/register/business" element={<LazyRoute><RegisterBusiness /></LazyRoute>} />
        <Route path="/register" element={<LazyRoute><Register /></LazyRoute>} />
        <Route path="/login/business" element={<LazyRoute><LoginBusiness /></LazyRoute>} />
        <Route path="/login/trader" element={<LazyRoute><LoginTrader /></LazyRoute>} />
        <Route path="/login" element={<LazyRoute><Login /></LazyRoute>} />
        <Route path="/trader/:slug" element={<LazyRoute><TraderPublicLanding /></LazyRoute>} />
        <Route path="/secure-access-panel-2024" element={<LazyRoute><AdminLogin /></LazyRoute>} />

        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <AdminPanel />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/blog" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <AdminBlogList />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/blog/new" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <AdminBlogEditor />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/blog/edit/:id" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BlogEditorWrapper />
          </ProtectedRoute>
        } />
        <Route path="/admin/businesses" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <BusinessList />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/businesses/edit/:businessId" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <BusinessEdit />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <UserList />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <SystemSettings />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/admin/trader-flows" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardErrorBoundary>
              <AdminTraderFlows />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/business" element={
          <ProtectedRoute allowedRoles={['business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="verified">
                <DashboardErrorBoundary>
                  <BusinessPanel />
                </DashboardErrorBoundary>
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/business/products" element={
          <ProtectedRoute allowedRoles={['business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="verified">
                <DashboardErrorBoundary>
                  <BusinessProducts />
                </DashboardErrorBoundary>
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/business/add-product" element={
          <ProtectedRoute allowedRoles={['business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="verified">
                <DashboardErrorBoundary>
                  <AddProduct />
                </DashboardErrorBoundary>
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/business/analytics" element={
          <ProtectedRoute allowedRoles={['business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="verified">
                <DashboardErrorBoundary>
                  <BusinessAnalytics />
                </DashboardErrorBoundary>
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/business-registration" element={
          <ProtectedRoute allowedRoles={['user', 'business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="registration">
                <BusinessRegistration />
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/business-pending" element={
          <ProtectedRoute allowedRoles={['business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="pending">
                <BusinessPendingStatus />
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/business-dashboard" element={
          <ProtectedRoute allowedRoles={['business']}>
            <LazyRoute>
              <BusinessAccessGate requiredPhase="verified">
                <DashboardErrorBoundary>
                  <BusinessPanel />
                </DashboardErrorBoundary>
              </BusinessAccessGate>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/merchant-pending" element={
          <ProtectedRoute allowedRoles={['trader']}>
            <LazyRoute><MerchantPendingStatus /></LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/merchant" element={
          <ProtectedRoute allowedRoles={['trader']}>
            <DashboardErrorBoundary>
              <MerchantPanel />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/merchant/products" element={
          <ProtectedRoute allowedRoles={['trader']}>
            <DashboardErrorBoundary>
              <BusinessProducts routePrefix="/merchant" />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/merchant/add-product" element={
          <ProtectedRoute allowedRoles={['trader']}>
            <DashboardErrorBoundary>
              <AddProduct routePrefix="/merchant" />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/supplier" element={
          <ProtectedRoute allowedRoles={['flasher_staff']}>
            <DashboardErrorBoundary>
              <SupplierHome />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/supplier/products" element={
          <ProtectedRoute allowedRoles={['flasher_staff']}>
            <DashboardErrorBoundary>
              <BusinessProducts routePrefix="/supplier" />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/supplier/add-product" element={
          <ProtectedRoute allowedRoles={['flasher_staff']}>
            <DashboardErrorBoundary>
              <AddProduct routePrefix="/supplier" />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/user" element={
          <ProtectedRoute allowedRoles={['user']}>
            <DashboardErrorBoundary>
              <UserDashboard />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/user/profile" element={
          <ProtectedRoute allowedRoles={['user']}>
            <DashboardErrorBoundary>
              <UserProfile />
            </DashboardErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="*" element={<NotFound />} />
      </Routes>
      {showMarketingChrome ? <Footer /> : null}
      </div>
    </div>
  );
}

function BlogEditorWrapper() {
  const { id } = useParams();
  return (
    <DashboardErrorBoundary>
      <AdminBlogEditor postId={id} />
    </DashboardErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <AppContent />
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
